import { useEffect, useMemo, useState } from "react";

type Stage = "kickoff" | "design" | "development" | "testing" | "launch";
type Status = "active" | "paused" | "completed" | "cancelled";

export interface ClientOption {
  id: string;
  company_name: string;
}

export interface TeamMemberOption {
  id: string;
  full_name: string;
}

export interface ProjectFormState {
  client_id: string;
  title: string;
  description: string;
  stage: Stage;
  status: Status;
  progress: number;
  budget: string;
  spent: string;
  start_date: string;
  due_date: string;
  tech_stack: string;
  team: string[];
  collaborators: string;
  screenshots: string[];
  thumbnail_url: string;
  video_url: string;
  video_type: string;
  featured: boolean;
  add_to_portfolio: boolean;
}


type Mode = "create" | "edit";

export default function ProjectModal({
  open,
  mode,
  clients,
  teamMembers,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: Mode;
  clients: ClientOption[];
  teamMembers: TeamMemberOption[];
  initial?: Partial<ProjectFormState> | null;
  onClose: () => void;
  onSubmit: (payload: {
    client_id: string;
    title: string;
    description?: string;
    stage: Stage;
    status: Status;
    progress: number;
    budget?: string;
    spent?: string;
    start_date?: string;
    due_date?: string;
    tech_stack?: string[];
    team: string[];
    collaborators?: string[];
    screenshots?: string[];
    thumbnail_url?: string;
    video_url?: string;
    video_type?: string;
    featured?: boolean;
    add_to_portfolio?: boolean;
  }) => Promise<void> | void;
}) {
  const title = useMemo(() => (mode === "create" ? "Add Project" : "Edit Project"), [mode]);

  const [form, setForm] = useState<ProjectFormState>({
    client_id: "",
    title: "",
    description: "",
    stage: "kickoff",
    status: "active",
    progress: 0,
    budget: "",
    spent: "",
    start_date: "",
    due_date: "",
    tech_stack: "",
    team: [],
    collaborators: "",
    screenshots: [],
    thumbnail_url: "",
    video_url: "",
    video_type: "youtube",
    featured: false,
    add_to_portfolio: false,
  });

  const parseList = (v: string) => v.split(",").map((x) => x.trim()).filter(Boolean);

  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setSubmitting(false);

    if (initial) {
      // Helper to safely convert value to string for inputs
      const toString = (val: any): string => {
        if (val === null || val === undefined) return "";
        if (Array.isArray(val)) return val.join(", ");
        if (typeof val === "string") return val;
        // fallback
        return String(val);
      };

      // Handle collaborators: use value directly if string, parse if needed
      let collaboratorsStr = "";
      if (initial.collaborators !== null && initial.collaborators !== undefined) {
        if (typeof initial.collaborators === "string") {
          // Try to parse as JSON array if it looks like JSON
          try {
            const parsed = JSON.parse(initial.collaborators);
            if (Array.isArray(parsed)) {
              collaboratorsStr = parsed.join(", ");
            } else {
              collaboratorsStr = initial.collaborators;
            }
          } catch {
            // Not valid JSON, use as-is
            collaboratorsStr = initial.collaborators;
          }
        } else if (Array.isArray(initial.collaborators)) {
          collaboratorsStr = (initial.collaborators as string[]).join(", ");
        } else {
          collaboratorsStr = String(initial.collaborators);
        }
      }
      // If initial.collaborators is null/undefined, collaboratorsStr remains "" (empty input)

      // Handle tech_stack: use value directly if string, parse if needed
      let techStackStr = "";
      if (initial.tech_stack !== null && initial.tech_stack !== undefined) {
        if (typeof initial.tech_stack === "string") {
          // Try to parse as JSON array if it looks like JSON
          try {
            const parsed = JSON.parse(initial.tech_stack);
            if (Array.isArray(parsed)) {
              techStackStr = parsed.join(", ");
            } else {
              techStackStr = initial.tech_stack;
            }
          } catch {
            // Not valid JSON, use as-is
            techStackStr = initial.tech_stack;
          }
        } else if (Array.isArray(initial.tech_stack)) {
          techStackStr = (initial.tech_stack as string[]).join(", ");
        } else {
          techStackStr = String(initial.tech_stack);
        }
      }
      // If initial.tech_stack is null/undefined, techStackStr remains "" (empty input)

      setForm({
        client_id: initial.client_id || "",
        title: initial.title || "",
        description: initial.description || "",
        stage: (initial.stage as Stage) || "kickoff",
        status: (initial.status as Status) || "active",
        progress: initial.progress || 0,
        budget: initial.budget || "",
        spent: initial.spent || "",
        start_date: initial.start_date || "",
        due_date: initial.due_date || "",
        tech_stack: techStackStr,
        team: Array.isArray(initial.team) ? initial.team : [],
        collaborators: collaboratorsStr,
        screenshots: Array.isArray(initial.screenshots) ? initial.screenshots : [],
        thumbnail_url: initial.thumbnail_url || "",
        video_url: initial.video_url || "",
        video_type: initial.video_type || "youtube",
        featured: initial.featured || false,
        add_to_portfolio: (initial as any).add_to_portfolio || false,
      });
    } else {
      setForm({
        client_id: clients[0]?.id ?? "",
        title: "",
        description: "",
        stage: "kickoff",
        status: "active",
        progress: 0,
        budget: "",
        spent: "",
        start_date: "",
        due_date: "",
        tech_stack: "",
        team: [],
        collaborators: "",
        screenshots: [],
        thumbnail_url: "",
        video_url: "",
        video_type: "youtube",
        featured: false,
        add_to_portfolio: false,
      });
    }

    setPassword("");
  }, [open, initial, clients]);


  if (!open) return null;

  const toggleTeamMember = (name: string) => {
    setForm((prev) => ({
      ...prev,
      team: prev.team.includes(name)
        ? prev.team.filter((t) => t !== name)
        : [...prev.team, name],
    }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setSubmitting(true);

      const payload: any = {
        client_id: form.client_id,
        title: form.title,
        description: form.description.trim() || "",
        stage: form.stage,
        status: form.status,
        progress: Math.max(0, Math.min(100, Number(form.progress) || 0)),
        budget: form.budget.trim() || "",
        spent: form.spent.trim() || "",
        start_date: form.start_date || "",
        due_date: form.due_date || "",
        team: form.team,
        thumbnail_url: form.thumbnail_url.trim() || "",
        video_url: form.video_url.trim() || "",
        video_type: form.video_type,
        featured: form.featured,
      };

      // Always send arrays, even when empty, so fields can be cleared.
      payload.tech_stack = parseList(form.tech_stack);
      payload.collaborators = parseList(form.collaborators);
      payload.screenshots = (form.screenshots || [])
        .map((s) => s.trim())
        .filter(Boolean);
      payload.add_to_portfolio = form.add_to_portfolio;

      await onSubmit(payload);
      setSubmitting(false);
      onClose();
    } catch (err: any) {
      setSubmitting(false);
      setError(err?.message || "Failed to save project");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => {
      if (teamDropdownOpen) {
        setTeamDropdownOpen(false);
      } else {
        onClose();
      }
    }}>
      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {title}
            </h3>
            <p className="text-silver text-sm mt-1">Fill the details and create the project.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Client</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.client_id}
                onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value }))}
                required
              >
                <option value="" disabled>
                  Select client
                </option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Title</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                required
                placeholder="Project title"
              />
            </label>


          </div>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Description</span>
            <textarea
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Project description"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Stage</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.stage}
                onChange={(e) => setForm((p) => ({ ...p, stage: e.target.value as Stage }))}
              >
                <option value="kickoff">kickoff</option>
                <option value="design">design</option>
                <option value="development">development</option>
                <option value="testing">testing</option>
                <option value="launch">launch</option>
              </select>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Status</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as Status }))}
              >
                <option value="active">active</option>
                <option value="paused">paused</option>
                <option value="completed">completed</option>
                <option value="cancelled">cancelled</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Progress (0-100)</span>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.progress}
                onChange={(e) => setForm((p) => ({ ...p, progress: Number(e.target.value) }))}
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Budget</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.budget}
                onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))}
                placeholder="Optional"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Spent</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.spent}
                onChange={(e) => setForm((p) => ({ ...p, spent: e.target.value }))}
                placeholder="Optional"
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Start date</span>
                <input
                  type="date"
                  className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={form.start_date}
                  onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                />
              </label>

              <label className="block">
                <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Due date</span>
                <input
                  type="date"
                  className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={form.due_date}
                  onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Tech stack</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.tech_stack}
                onChange={(e) => setForm((p) => ({ ...p, tech_stack: e.target.value }))}
                placeholder="e.g. React, Node, Postgres"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Collaborators</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.collaborators}
                onChange={(e) => setForm((p) => ({ ...p, collaborators: e.target.value }))}
                placeholder="Comma-separated names/emails"
              />
            </label>


            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Team</span>
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={() => setTeamDropdownOpen(!teamDropdownOpen)}
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-left text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors flex items-center justify-between"
                >
                  <span>
                    {(form.team || []).length === 0
                      ? "Select team members"
                      : `${(form.team || []).length} selected`}
                  </span>
                  <i className={`ri-arrow-${teamDropdownOpen ? "up" : "down"}-s-line`} />
                </button>
                {teamDropdownOpen && (
                  <div className="absolute top-full mt-1 z-20 bg-dark-card border border-dark-border rounded-xl shadow-xl min-w-[280px] max-h-48 overflow-y-auto">
                      {teamMembers.map((member) => (
                        <label
                          key={member.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-dark-panel cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={form.team.includes(member.full_name)}
                            onChange={() => toggleTeamMember(member.full_name)}
                            className="w-4 h-4 text-electric-blue bg-dark-card border-dark-border rounded focus:ring-electric-blue/50"
                          />
                          <span className="text-white text-sm">{member.full_name}</span>
                        </label>
                      ))}
                    </div>
                )}
                {/* Show selected team members with remove button */}
                {(form.team || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(form.team || []).map((memberName) => (
                      <div
                        key={memberName}
                        className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-lg px-3 py-1.5"
                      >
                        <span className="text-white text-sm">{memberName}</span>
                        <button
                          type="button"
                          onClick={() => toggleTeamMember(memberName)}
                          className="text-silver-dim hover:text-cyber-red transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Thumbnail URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.thumbnail_url}
                onChange={(e) => setForm((p) => ({ ...p, thumbnail_url: e.target.value }))}
                placeholder="https://example.com/image.jpg"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Video URL</span>
              <input
                type="url"
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.video_url}
                onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=..."
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Video Type</span>
              <select
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.video_type}
                onChange={(e) => setForm((p) => ({ ...p, video_type: e.target.value }))}
              >
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
                <option value="direct">Direct Link</option>
              </select>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Featured</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm((p) => ({ ...p, featured: e.target.checked }))}
                  className="w-4 h-4 text-electric-blue bg-dark-card border-dark-border rounded focus:ring-electric-blue/50"
                />
                <span className="text-silver text-sm">Show in portfolio</span>
              </div>
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Portfolio</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.add_to_portfolio}
                  onChange={(e) => setForm((p) => ({ ...p, add_to_portfolio: e.target.checked }))}
                  className="w-4 h-4 text-electric-blue bg-dark-card border-dark-border rounded focus:ring-electric-blue/50"
                />
                <span className="text-silver text-sm">Add to portfolio</span>
              </div>
            </label>
          </div>

          <div>
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Screenshots</span>
            <div className="space-y-2 mt-1">
              {form.screenshots.map((url, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="url"
                    className="flex-1 bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                    value={url}
                    onChange={(e) => {
                      const next = [...form.screenshots];
                      next[idx] = e.target.value;
                      setForm((p) => ({ ...p, screenshots: next }));
                    }}
                    placeholder="https://example.com/screenshot.jpg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.screenshots.filter((_, i) => i !== idx);
                      setForm((p) => ({ ...p, screenshots: next }));
                    }}
                    className="px-3 rounded-xl border border-dark-border text-silver hover:text-cyber-red hover:border-cyber-red/40 transition-colors cursor-pointer"
                    aria-label="Remove screenshot"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, screenshots: [...p.screenshots, ""] }))}
                className="flex items-center gap-2 text-sm text-electric-blue hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-add-line" /> Add screenshot URL
              </button>
            </div>
          </div>

          {error && <div className="text-cyber-orange text-sm">{error}</div>}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                submitting ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
              }`}
            >
              {submitting ? "Saving..." : mode === "create" ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}